import React, { createContext, useContext, useState, useCallback, useMemo, useReducer } from 'react'

// Create the booking context
const BookingContext = createContext()

// Custom hook to use the booking context
export const useBookingContext = () => {
  const context = useContext(BookingContext)
  if (!context) {
    throw new Error('useBookingContext must be used within a BookingProvider')
  }
  return context
}

// Booking reducer for complex state management
const bookingReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload }

    case 'SET_APPOINTMENT_DATA':
      return {
        ...state,
        appointmentData: { ...state.appointmentData, ...action.payload }
      }

    case 'SET_VALIDATION_ERRORS':
      return { ...state, validationErrors: action.payload }

    case 'SET_BOOKING_STATUS':
      return { ...state, bookingStatus: action.payload }

    case 'ADD_TO_BOOKING_HISTORY':
      return {
        ...state,
        bookingHistory: [action.payload, ...state.bookingHistory].slice(0, 20)
      }

    case 'RESET_BOOKING_FLOW':
      return {
        ...state,
        currentStep: 'doctor-selection',
        appointmentData: {},
        validationErrors: {},
        bookingStatus: 'idle'
      }

    default:
      return state
  }
}

// Initial booking state
const initialBookingState = {
  currentStep: 'doctor-selection', // doctor-selection, time-selection, confirmation, success
  appointmentData: {},
  validationErrors: {},
  bookingStatus: 'idle', // idle, submitting, success, error
  bookingHistory: []
}

// Booking provider component
export const BookingProvider = ({ children }) => {
  // Use reducer for complex booking flow state
  const [bookingState, dispatch] = useReducer(bookingReducer, initialBookingState)

  // Simple state for UI interactions
  const [userPreferences, setUserPreferences] = useState({
    preferredTimeSlots: [],
    preferredDoctors: [],
    medicalAidInfo: null,
    contactPreferences: {
      email: true,
      sms: false,
      phone: false
    }
  })

  // Temporary booking data (for when user is browsing)
  const [tempBookingData, setTempBookingData] = useState({
    selectedDoctor: null,
    availableSlots: [],
    selectedSlot: null,
    reason: null
  })

  // UI state
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [bookingModalStep, setBookingModalStep] = useState(1)

  // Booking flow management
  const setCurrentStep = useCallback((step) => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: step })
  }, [])

  const updateAppointmentData = useCallback((data) => {
    dispatch({ type: 'SET_APPOINTMENT_DATA', payload: data })
  }, [])

  const setValidationErrors = useCallback((errors) => {
    dispatch({ type: 'SET_VALIDATION_ERRORS', payload: errors })
  }, [])

  const setBookingStatus = useCallback((status) => {
    dispatch({ type: 'SET_BOOKING_STATUS', payload: status })
  }, [])

  // Validation functions
  const validateBookingData = useCallback(
    (data = bookingState.appointmentData) => {
      const errors = {}

      if (!data.doctor) {
        errors.doctor = 'Please select a doctor'
      }

      if (!data.date) {
        errors.date = 'Please select a date'
      }

      if (!data.time) {
        errors.time = 'Please select a time'
      }

      if (!data.reason) {
        errors.reason = 'Please provide a reason for the visit'
      }

      if (data.reason && data.reason.length < 5) {
        errors.reason = 'Please provide more details about your visit'
      }

      setValidationErrors(errors)
      return Object.keys(errors).length === 0
    },
    [bookingState.appointmentData, setValidationErrors]
  )

  // Booking completion
  const completeBooking = useCallback(
    (bookingResult) => {
      dispatch({
        type: 'ADD_TO_BOOKING_HISTORY',
        payload: {
          id: bookingResult.id,
          ...bookingState.appointmentData,
          bookedAt: new Date(),
          status: 'confirmed'
        }
      })

      // Save to localStorage for persistence
      const existingHistory = JSON.parse(localStorage.getItem('bookingHistory') || '[]')
      const updatedHistory = [
        {
          id: bookingResult.id,
          ...bookingState.appointmentData,
          bookedAt: new Date(),
          status: 'confirmed'
        },
        ...existingHistory
      ].slice(0, 20)

      localStorage.setItem('bookingHistory', JSON.stringify(updatedHistory))

      setBookingStatus('success')
      setCurrentStep('success')
    },
    [bookingState.appointmentData, setBookingStatus, setCurrentStep]
  )

  const resetBookingFlow = useCallback(() => {
    dispatch({ type: 'RESET_BOOKING_FLOW' })
    setTempBookingData({
      selectedDoctor: null,
      availableSlots: [],
      selectedSlot: null,
      reason: null
    })
  }, [])

  // User preferences management
  const updateUserPreferences = useCallback(
    (preferences) => {
      setUserPreferences((prev) => ({ ...prev, ...preferences }))
      localStorage.setItem('userPreferences', JSON.stringify({ ...userPreferences, ...preferences }))
    },
    [userPreferences]
  )

  const addPreferredDoctor = useCallback((doctorId) => {
    setUserPreferences((prev) => ({
      ...prev,
      preferredDoctors: [...new Set([...prev.preferredDoctors, doctorId])]
    }))
  }, [])

  const removePreferredDoctor = useCallback((doctorId) => {
    setUserPreferences((prev) => ({
      ...prev,
      preferredDoctors: prev.preferredDoctors.filter((id) => id !== doctorId)
    }))
  }, [])

  // Temporary booking data management
  const updateTempBookingData = useCallback((data) => {
    setTempBookingData((prev) => ({ ...prev, ...data }))
  }, [])

  const clearTempBookingData = useCallback(() => {
    setTempBookingData({
      selectedDoctor: null,
      availableSlots: [],
      selectedSlot: null,
      reason: null
    })
  }, [])

  // Modal management
  const openBookingModal = useCallback((step = 1) => {
    setShowBookingModal(true)
    setBookingModalStep(step)
  }, [])

  const closeBookingModal = useCallback(() => {
    setShowBookingModal(false)
    setBookingModalStep(1)
  }, [])

  // Utility functions
  const isBookingComplete = useMemo(() => {
    const { doctor, date, time, reason } = bookingState.appointmentData
    return !!(doctor && date && time && reason)
  }, [bookingState.appointmentData])

  const getBookingProgress = useMemo(() => {
    const steps = ['doctor-selection', 'time-selection', 'confirmation', 'success']
    const currentIndex = steps.indexOf(bookingState.currentStep)
    return ((currentIndex + 1) / steps.length) * 100
  }, [bookingState.currentStep])

  const canProceedToNextStep = useMemo(() => {
    switch (bookingState.currentStep) {
      case 'doctor-selection':
        return !!bookingState.appointmentData.doctor
      case 'time-selection':
        return !!(bookingState.appointmentData.date && bookingState.appointmentData.time)
      case 'confirmation':
        return isBookingComplete && Object.keys(bookingState.validationErrors).length === 0
      default:
        return false
    }
  }, [bookingState, isBookingComplete])

  // Load saved data on mount
  React.useEffect(() => {
    // Load user preferences
    const savedPreferences = localStorage.getItem('userPreferences')
    if (savedPreferences) {
      try {
        setUserPreferences(JSON.parse(savedPreferences))
      } catch (error) {
        console.error('Error loading user preferences:', error)
      }
    }

    // Load booking history
    const savedHistory = localStorage.getItem('bookingHistory')
    if (savedHistory) {
      try {
        const history = JSON.parse(savedHistory)
        dispatch({ type: 'SET_BOOKING_HISTORY', payload: history })
      } catch (error) {
        console.error('Error loading booking history:', error)
      }
    }
  }, [])

  // Context value
  const contextValue = useMemo(
    () => ({
      // Booking state
      ...bookingState,
      userPreferences,
      tempBookingData,
      showBookingModal,
      bookingModalStep,

      // Computed values
      isBookingComplete,
      getBookingProgress,
      canProceedToNextStep,

      // Booking flow actions
      setCurrentStep,
      updateAppointmentData,
      setValidationErrors,
      setBookingStatus,
      validateBookingData,
      completeBooking,
      resetBookingFlow,

      // User preferences actions
      updateUserPreferences,
      addPreferredDoctor,
      removePreferredDoctor,

      // Temporary booking actions
      updateTempBookingData,
      clearTempBookingData,

      // Modal actions
      openBookingModal,
      closeBookingModal,
      setBookingModalStep,

      // Direct state setters (for advanced use cases)
      setUserPreferences,
      setTempBookingData,
      setShowBookingModal
    }),
    [
      bookingState,
      userPreferences,
      tempBookingData,
      showBookingModal,
      bookingModalStep,
      isBookingComplete,
      getBookingProgress,
      canProceedToNextStep,
      setCurrentStep,
      updateAppointmentData,
      setValidationErrors,
      setBookingStatus,
      validateBookingData,
      completeBooking,
      resetBookingFlow,
      updateUserPreferences,
      addPreferredDoctor,
      removePreferredDoctor,
      updateTempBookingData,
      clearTempBookingData,
      openBookingModal,
      closeBookingModal
    ]
  )

  return <BookingContext.Provider value={contextValue}>{children}</BookingContext.Provider>
}

// Higher-order component for easy integration
export const withBookingContext = (Component) => {
  return function WrappedComponent(props) {
    return (
      <BookingProvider>
        <Component {...props} />
      </BookingProvider>
    )
  }
}

export default BookingContext
